import React, { useState } from 'react';
import {
    format,
    subMonths,
    addMonths,
    startOfMonth,
    endOfMonth,
    startOfWeek,
    addDays,
    isSameMonth,
    isSameDay,
    isAfter,
    isBefore,
    isWithinInterval
} from 'date-fns';
import SettingButton from '../Button/SettingButton';

const DateRange = ({ onClose, handleDateClick, selectedRange, generateReport , loader }) => {
    const now = new Date();
    const threeMonthsAgo = subMonths(now, 3);

    // const [selectedRange, setSelectedRange] = useState({ start: null, end: null });
    const [leftMonth, setLeftMonth] = useState(subMonths(now, 1));
    const [rightMonth, setRightMonth] = useState(now);

    // const handleDateClick = (date) => {
    //     if (date < threeMonthsAgo || date > now) return;

    //     if (!selectedRange.start || (selectedRange.start && selectedRange.end)) {
    //         setSelectedRange({ start: date, end: null });
    //     } else if (date < selectedRange.start) {
    //         setSelectedRange({ start: date, end: selectedRange.start });
    //     } else {
    //         setSelectedRange({ ...selectedRange, end: date });
    //     }
    // };

    const generateCalendar = (month) => {
        const startDate = startOfWeek(startOfMonth(month), { weekStartsOn: 0 });
        const endDate = endOfMonth(month);
        const rows = [];

        let day = startDate;
        while (day <= endDate || rows.length < 6) {
            const week = [];
            for (let i = 0; i < 7; i++) {
                week.push(day);
                day = addDays(day, 1);
            }
            rows.push(week);
        }

        return rows;
    };

    const isInRange = (day) => {
        const { start, end } = selectedRange;
        return start && end && isWithinInterval(day, { start, end });
    };

    const isDisabled = (day) => day < threeMonthsAgo || day > now;

    const renderCalendar = (monthDate, isLeftCalendar = true) => {
        const calendar = generateCalendar(monthDate);
        const monthTitle = format(monthDate, 'MMMM yyyy');
        const daysOfWeek = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

        const canNavigateLeft = isLeftCalendar && subMonths(monthDate, 1) >= threeMonthsAgo;
        const canNavigateRight = !isLeftCalendar && addMonths(monthDate, 1) <= now;

        return (
            <div className="relative  ">
                <div className="flex items-center  justify-between mb-2 ">
                    {isLeftCalendar && (
                        <button
                            onClick={() => {
                                const prev = subMonths(monthDate, 1);
                                setLeftMonth(prev);
                                setRightMonth(subMonths(prev, -1));
                            }}
                            className={`p-1 rounded-lg border ${canNavigateLeft ? 'hover:bg-gray-100' : 'opacity-30 cursor-not-allowed'}`}
                            disabled={!canNavigateLeft}
                        >
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M10 13L5 8L10 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </button>
                    )}

                    <h3 className="text-center font-medium text-gray-800">{monthTitle}</h3>

                    {!isLeftCalendar && (
                        <button
                            onClick={() => {
                                const next = addMonths(monthDate, 1);
                                setRightMonth(next);
                                setLeftMonth(subMonths(next, 1));
                            }}
                            className={`p-1 rounded-lg border ${canNavigateRight ? 'hover:bg-gray-100' : 'opacity-30 cursor-not-allowed'}`}
                            disabled={!canNavigateRight}
                        >
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M6 13L11 8L6 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </button>
                    )}
                </div>

                <div className="grid grid-cols-7 gap-1 text-center">
                    {daysOfWeek.map(day => (
                        <div key={day} className="text-xs text-gray-500 font-medium py-1">{day}</div>
                    ))}
                    {calendar.map((week, i) => (
                        <React.Fragment key={i}>
                            {week.map((day, idx) => {
                                const selected = selectedRange.start && isSameDay(day, selectedRange.start) ||
                                    selectedRange.end && isSameDay(day, selectedRange.end);
                                const inRange = isInRange(day);
                                const disabled = isDisabled(day);
                                const isCurrentMonth = isSameMonth(day, monthDate);

                                let bgClass = '';
                                let textClass = '';
                                let roundedClass = '';

                                if (selected) {
                                    bgClass = 'bg-black';
                                    textClass = 'text-white';
                                    roundedClass = 'rounded-lg';
                                } else if (inRange) {
                                    bgClass = 'bg-gray-100';
                                    textClass = 'text-black';
                                    roundedClass = 'rounded-lg';

                                    if (selectedRange.start && isSameDay(day, selectedRange.start)) {
                                        roundedClass = 'rounded-l-full';
                                    } else if (selectedRange.end && isSameDay(day, selectedRange.end)) {
                                        roundedClass = 'rounded-r-full';
                                    }
                                } else if (disabled) {
                                    textClass = 'text-gray-300';
                                } else if (!isCurrentMonth) {
                                    textClass = 'text-gray-400';
                                } else {
                                    textClass = 'text-gray-800 hover:bg-gray-100';
                                }

                                return (
                                    <div
                                        key={idx}
                                        onClick={() => !disabled && handleDateClick(day)}
                                        className={`py-1 text-sm cursor-pointer ${bgClass} ${textClass} ${roundedClass}`}
                                    >
                                        {format(day, 'd')}
                                    </div>
                                );
                            })}
                        </React.Fragment>
                    ))}
                </div>
            </div>
        );
    };

    return (
        <div className=" mx-auto p-6 bg-white rounded-lg shadow-md w-[768px]">
            <h1 className="text-lg font-semibold text-gray-800 mb-4">Select date range</h1>

            <div className="flex items-center mb-4 gap-2">
                <p className="text-sm text-gray-500">Selected Range:</p>
                <p className="text-sm text-gray-500 font-medium">
                    {selectedRange.start ? format(selectedRange.start, 'MMM d, yyyy') : 'Start'} – {selectedRange.end ? format(selectedRange.end, 'MMM d, yyyy') : 'End'}
                </p>
            </div>

            {/* Two Calendar View */}
            <div className="grid grid-cols-2 gap-4 border rounded-lg p-4">
                {renderCalendar(leftMonth, true)}
                {renderCalendar(rightMonth, false)}
            </div>



            {/* Footer Buttons */}
            <div className="flex justify-start space-x-3 pt-2 border-gray-200 mt-2">
                <button
                    id='generate_report_confirm'
                    disabled={!selectedRange.start || !selectedRange.end}
                    className={`inline-flex items-center justify-center px-12 py-3 text-sm font-medium rounded-md ${selectedRange.start && selectedRange.end
                        ? 'bg-[#111322] text-white hover:bg-gray-900'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        }`}
                    onClick={generateReport}
                >
                    {loader ? (
                         <svg
                         className="animate-spin h-6 w-6  text-white"
                         xmlns="http://www.w3.org/2000/svg"
                         fill="none"
                         viewBox="0 0 24 24"
                     >
                         <circle
                             className="opacity-25"
                             cx="12"
                             cy="12"
                             r="10"
                             stroke="currentColor"
                             strokeWidth="4"
                         ></circle>
                         <path
                             className="opacity-75"
                             fill="currentColor"
                             d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 2.042.784 3.903 2.071 5.291l1.929-1.929z"
                         ></path>
                     </svg>
                    ) : (
                        'Generate'
                    )}
                </button>
                <button
                    onClick={onClose}
                    className="px-12 py-3 text-sm font-medium text-gray-700 border bg-white rounded-md hover:bg-gray-200"
                >
                    Cancel
                </button>

            </div>
        </div>
    );
};

export default DateRange;